import { LoadedBasemap, LoadedModel } from "@/lib/cesium/types";
import { create } from "zustand";

interface CesiumStoreState {
  // État des modèles
  loadedModels: Map<string, LoadedModel>;
  visibleModels: Set<string>;
  loadedBasemap: LoadedBasemap | null;
  basemapVisible: boolean;
  basemapOpacity: number;
  basemapLoading: boolean;

  // État des opérations
  isLoading: boolean;
  error: string | null;

  // Actions sur les modèles
  addLoadedModel: (model: LoadedModel) => void;
  removeLoadedModel: (id: string) => void;
  setModelVisibility: (id: string, visible: boolean) => void;
  setModelOpacity: (id: string, opacity: number) => void;
  setLoadedBasemap: (basemap: LoadedBasemap | null) => void;
  setBasemapVisibility: (visible: boolean) => void;
  setBasemapOpacity: (opacity: number) => void;
  setBasemapLoading: (loading: boolean) => void;
  unloadBasemap: () => void;

  // Actions sur l'état
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Reset
  reset: () => void;
}

export const useCesiumStore = create<CesiumStoreState>((set) => ({
  // État initial
  loadedModels: new Map(),
  visibleModels: new Set(),
  loadedBasemap: null,
  basemapVisible: true,
  basemapOpacity: 1,
  basemapLoading: false,
  isLoading: false,
  error: null,

  // Actions
  addLoadedModel: (model: LoadedModel) =>
    set((state) => {
      const newModels = new Map(state.loadedModels);
      newModels.set(model.id, model);
      return { loadedModels: newModels };
    }),

  removeLoadedModel: (id: string) =>
    set((state) => {
      const newModels = new Map(state.loadedModels);
      const newVisible = new Set(state.visibleModels);
      newModels.delete(id);
      newVisible.delete(id);
      return { loadedModels: newModels, visibleModels: newVisible };
    }),

  setModelVisibility: (id: string, visible: boolean) =>
    set((state) => {
      const newVisible = new Set(state.visibleModels);
      if (visible) {
        newVisible.add(id);
      } else {
        newVisible.delete(id);
      }
      return { visibleModels: newVisible };
    }),

  setModelOpacity: (id: string, opacity: number) =>
    set((state) => {
      const newModels = new Map(state.loadedModels);
      const model = newModels.get(id);
      if (model) {
        model.opacity = Math.max(0, Math.min(1, opacity));
        newModels.set(id, model);
      }
      return { loadedModels: newModels };
    }),

  setLoadedBasemap: (basemap: LoadedBasemap | null) =>
    set({ loadedBasemap: basemap }),

  setBasemapVisibility: (visible: boolean) => set({ basemapVisible: visible }),

  setBasemapOpacity: (opacity: number) =>
    set({ basemapOpacity: Math.max(0, Math.min(1, opacity)) }),

  setBasemapLoading: (loading: boolean) => set({ basemapLoading: loading }),

  unloadBasemap: () =>
    set((state) => {
      if (!state.loadedBasemap) {
        return { loadedBasemap: null };
      }

      const newModels = new Map(state.loadedModels);
      const newVisible = new Set(state.visibleModels);

      for (const tileset of state.loadedBasemap.tilesets) {
        const modelId = `tileset-${tileset.id}`;
        newModels.delete(modelId);
        newVisible.delete(modelId);
      }

      return {
        loadedModels: newModels,
        visibleModels: newVisible,
        loadedBasemap: null,
        basemapVisible: false,
        basemapLoading: false,
      };
    }),

  setLoading: (loading: boolean) => set({ isLoading: loading }),

  setError: (error: string | null) => set({ error }),

  clearError: () => set({ error: null }),

  reset: () =>
    set({
      loadedModels: new Map(),
      visibleModels: new Set(),
      loadedBasemap: null,
      basemapVisible: true,
      basemapOpacity: 1,
      basemapLoading: false,
      isLoading: false,
      error: null,
    }),
}));
